import { alpha } from '@mui/material/styles';
import React from 'react';
import { Box, Skeleton, Typography } from '@mui/material';
import moment from 'moment';
import { DASHBOARD_ACCENT } from './dashboardSurfaceSx';
import { calendarDataBoneSx } from './dashboardCalendarLoading';
import { ROOM_BOARD_ACCENT } from './roomBoardStatusColors';
import { isSameDayStay } from './bookingStayDisplay';

/**
 * Room Board inventory summary (per room on Ultra / per room-night on bar calendar):
 *
 * - Grid 7/15/30: stats sum every visible day (Total = rooms × days).
 * - Booked: confirmed / reserved / pending / web_checkin stay nights
 * - In House: checked_in / partial_checkin stay nights (never Due, never Out)
 * - Due: departure morning while guest still in-house, only on/after hotel today
 * - Out: checked_out stay nights (same night basis as Booked / In House)
 * - Occupancy: (In House + Booked + Out + Due) ÷ sellable
 * - Available: open + out + due; blocked never available
 */
export function emptyRoomBoardGroupCounts(total = 0) {
  return {
    total,
    occupied: 0,
    hourly: 0,
    pending_checkout: 0,
    pending_with_next: 0,
    pending_free: 0,
    booked: 0,
    blocked: 0,
    vacant: 0,
    checked_out: 0,
    cancelled: 0,
    // Optional: rooms that departed today (may exceed rooms still showing OUT after turnover).
    departures_today: null,
  };
}

export function buildRoomBoardSummary(groupCounts) {
  const total = groupCounts.total || 0;
  const blocked = groupCounts.blocked || 0;
  const occupied = groupCounts.occupied || 0;
  const hourly = groupCounts.hourly || 0;
  const bookedDisplay = groupCounts.booked || 0;
  const pendingWithNext = groupCounts.pending_with_next || 0;
  const pendingFree = groupCounts.pending_free || 0;
  const open = (groupCounts.vacant || 0) + (groupCounts.cancelled || 0);
  // Rooms currently painted as OUT (physical availability).
  const outDisplay = groupCounts.checked_out || 0;
  // Out tile + occupancy: prefer today's departure count when provided (includes
  // rooms that already turned over to a new in-house / booked stay).
  const departuresRaw = groupCounts.departures_today;
  const hasDepartures =
    departuresRaw != null && departuresRaw !== '' && Number.isFinite(Number(departuresRaw));
  const out = hasDepartures
    ? Math.max(outDisplay, Number(departuresRaw))
    : outDisplay;

  const inHouse = occupied + hourly;
  const booked = bookedDisplay + pendingWithNext;
  // Available uses rooms free now — not every departure (turnover rooms are occupied again).
  const available = open + outDisplay + pendingFree;
  const sellable = Math.max(total - blocked, 0);
  // Sold for the day includes all today's departures — even after same-day re-occupancy.
  const sold = inHouse + booked + out + pendingFree;
  const occupancyPct = sellable > 0
    ? Number(((sold / sellable) * 100).toFixed(2))
    : 0;

  return {
    total,
    blocked,
    inHouse,
    booked,
    available,
    open,
    out,
    due: pendingFree,
    occupied,
    hourly,
    pendingWithNext,
    sellable,
    sold,
    occupancyPct,
  };
}

function normalizeBarStatus(status) {
  return String(status || '').toLowerCase().replace(/\s+/g, '_');
}

function isBlockBar(bar) {
  const st = normalizeBarStatus(bar?.status);
  return st === 'blocked' || Boolean(bar?.block_type) || Boolean(bar?.isBlock);
}

function isArrivalOrBookedStatus(st) {
  return ['confirmed', 'booked', 'pending', 'reserved', 'web_checkin'].includes(st);
}

/** Checked-in guest (In House on stay nights). */
function isCheckedInStatus(st) {
  return st === 'checked_in' || st === 'partial_checkin';
}

/** Still occupying the room (stay nights); includes partial_checkout before departure day. */
function isActiveStayStatus(st, bar) {
  if (isCheckedInStatus(st)) return true;
  if (st === 'partial_checkout' && bar?.blocksRoomForAllocation !== false) return true;
  return false;
}

function barPriority(bar) {
  const st = normalizeBarStatus(bar?.status);
  if (isBlockBar(bar)) return 100;
  if (isActiveStayStatus(st, bar) || isCheckedInStatus(st)) return 80;
  if (isArrivalOrBookedStatus(st)) return 60;
  if (st === 'checked_out' || (st === 'partial_checkout' && bar?.blocksRoomForAllocation === false)) return 40;
  if (st === 'cancelled' || st === 'canceled' || st === 'no_show') return 20;
  return 10;
}

function getBarCols(bar) {
  const startCol = Number(bar?.startCol) || 0;
  const span = Math.max(1, Number(bar?.span) || 1);
  const endCol = startCol + span - 1;
  return { startCol, endCol, span };
}

function isSameDayBar(bar) {
  if (bar?.booking && isSameDayStay(bar.booking)) return true;
  if (bar?.startDate && bar?.endDate) {
    return moment(bar.startDate).isSame(moment(bar.endDate), 'day');
  }
  const { span, startCol, endCol } = getBarCols(bar);
  return span <= 1 && startCol === endCol && !bar?.extendsBeyondCalendar && !bar?.startsBeforeCalendar;
}

/**
 * Stay nights: check-in through the night before checkout.
 * Booking 28→checkout 8, calendar 1–7 (checkout outside) ⇒ nights on days 1–7 (7 nights).
 */
function barCoversStayNight(bar, focusCol) {
  const { startCol, endCol } = getBarCols(bar);
  if (focusCol < startCol || focusCol > endCol) return false;
  if (isSameDayBar(bar)) return focusCol === startCol;
  // Checkout column inside the grid is departure day, not a stay night.
  if (!bar?.extendsBeyondCalendar && focusCol === endCol) return false;
  return true;
}

/** True when focusCol is the booking's checkout day and that day is visible. */
function barIsCheckoutDay(bar, focusCol) {
  if (isSameDayBar(bar)) return focusCol === getBarCols(bar).startCol;
  if (bar?.extendsBeyondCalendar) return false;
  const { endCol } = getBarCols(bar);
  return focusCol === endCol;
}

function barsOverlapFocus(bar, focusCol) {
  const { startCol, endCol } = getBarCols(bar);
  return focusCol >= startCol && focusCol <= endCol;
}

function isDepartedBarStatus(st, bar) {
  return st === 'checked_out' || (st === 'partial_checkout' && bar?.blocksRoomForAllocation === false);
}

function roomBars(room, bookingBars) {
  return (
    bookingBars?.[room.id]
    || bookingBars?.[String(room.id)]
    || bookingBars?.[Number(room.id)]
    || []
  );
}

function hasSameDayNextBooking(primary, pool) {
  return pool.some((bar) => {
    if (bar === primary) return false;
    if (isBlockBar(bar)) return false;
    const otherId = bar.booking?.id ?? bar.booking?.booking_id;
    const primaryId = primary.booking?.id ?? primary.booking?.booking_id;
    if (otherId != null && primaryId != null && Number(otherId) === Number(primaryId)) {
      return false;
    }
    const otherSt = normalizeBarStatus(bar.status);
    return isArrivalOrBookedStatus(otherSt) || isCheckedInStatus(otherSt) || isActiveStayStatus(otherSt, bar);
  });
}

/** True when this bar has at least one stay night inside the visible calendar columns. */
function barHasStayNightInVisibleRange(bar, rangeDays) {
  const days = Math.max(0, Number(rangeDays) || 0);
  for (let col = 0; col < days; col += 1) {
    if (barCoversStayNight(bar, col)) return true;
  }
  return false;
}

/**
 * One room × one calendar day.
 *
 * Stay night: Booked | In House | Out (checked-out) — all night-based.
 * Checkout day: Due (still in-house on/after today) | Open (future departure / departed).
 * Departure morning is not an extra Out night when stay nights are already in-range.
 * Exception: if the stay nights fall before the visible range (e.g. calendar starts on
 * checkout day), count departure morning as Out so those checkouts are not dropped.
 *
 * @param {{ dayKey?: string|null, todayKey?: string|null, rangeDays?: number }} [dayContext]
 *   dayKey/todayKey as YYYY-MM-DD. Due only when dayKey <= todayKey (pending/overdue).
 */
function classifyRoomForFocusCol(room, bookingBars, focusCol = 0, dayContext = null) {
  const bars = roomBars(room, bookingBars);
  const overlapping = bars.filter((bar) => barsOverlapFocus(bar, focusCol));
  const dayKey = dayContext?.dayKey || null;
  const todayKey = dayContext?.todayKey || null;
  const rangeDays = Number(dayContext?.rangeDays);

  if (!overlapping.length) {
    return { status: 'vacant', hasNextBooking: false };
  }

  // Inventory block bars use half-open stay geometry (endDate = unblock date).
  // Count only real block nights — same rule as stay nights — or a 1-day block
  // would paint into the next column.
  if (overlapping.some((bar) => isBlockBar(bar) && barCoversStayNight(bar, focusCol))) {
    return { status: 'blocked', hasNextBooking: false };
  }

  const stayNightBars = overlapping.filter((bar) => barCoversStayNight(bar, focusCol));
  const checkoutBars = overlapping.filter((bar) => barIsCheckoutDay(bar, focusCol));

  // Stay nights first (In House / Booked / Out). Due only on checkout day below.
  // Skip cancelled so a lower-priority Out/In House bar can still win.
  if (stayNightBars.length) {
    const ranked = [...stayNightBars].sort((a, b) => barPriority(b) - barPriority(a));
    for (let i = 0; i < ranked.length; i += 1) {
      const primary = ranked[i];
      const st = normalizeBarStatus(primary.status);
      const sameDay = isSameDayBar(primary);

      if (st === 'cancelled' || st === 'canceled' || st === 'no_show') {
        continue;
      }
      if (isActiveStayStatus(st, primary) || isCheckedInStatus(st)) {
        // Same-day stay: hourly In House. Multi-night: occupied In House.
        // Due is only assigned on checkout day (not here).
        return {
          status: sameDay ? 'hourly' : 'occupied',
          hasNextBooking: false,
        };
      }
      if (isArrivalOrBookedStatus(st)) {
        return { status: 'booked', hasNextBooking: false };
      }
      if (isDepartedBarStatus(st, primary)) {
        // Checked-out stay nights = Out (3-night stay ⇒ Out 3), same basis as Booked.
        return { status: 'checked_out', hasNextBooking: false };
      }
    }
  }

  if (checkoutBars.length) {
    const ranked = [...checkoutBars].sort((a, b) => barPriority(b) - barPriority(a));
    for (let i = 0; i < ranked.length; i += 1) {
      const primary = ranked[i];
      const st = normalizeBarStatus(primary.status);
      const next = hasSameDayNextBooking(primary, overlapping);

      if (st === 'cancelled' || st === 'canceled' || st === 'no_show') {
        continue;
      }

      if (isDepartedBarStatus(st, primary)) {
        // Stay nights already in this range ⇒ departure morning is open (not double-counted).
        // Stay nights only before the range (calendar starts on CO day) ⇒ count as Out.
        const stayNightsVisible = Number.isFinite(rangeDays) && rangeDays > 0
          ? barHasStayNightInVisibleRange(primary, rangeDays)
          : true;
        if (!stayNightsVisible) {
          return { status: 'checked_out', hasNextBooking: false };
        }
        return { status: 'vacant', hasNextBooking: false };
      }
      if (isActiveStayStatus(st, primary) || isCheckedInStatus(st)) {
        // Future checkout mornings in a multi-day board are not "Due" yet
        // (e.g. in-house Aug 1→2 must not add Due on Aug 2 while today is Aug 1).
        if (todayKey && dayKey && String(dayKey) > String(todayKey)) {
          return { status: 'vacant', hasNextBooking: false };
        }
        // Due — pending checkout today / overdue. Not In House (avoids double counting).
        return { status: 'pending_checkout', hasNextBooking: next };
      }
      // Confirmed/reserved bar still paints into the checkout column (mid-day),
      // but that morning is NOT a sold night — only check-in → night before checkout.
      // Example: 12→15 confirmed ⇒ nights 12,13,14 (3), day 15 vacant/open.
    }
  }

  return { status: 'vacant', hasNextBooking: false };
}

function applyRoomStatusToCounts(counts, status, hasNextBooking) {
  counts[status] += 1;
  if (status === 'pending_checkout') {
    if (hasNextBooking) counts.pending_with_next += 1;
    else counts.pending_free += 1;
  }
}

function barCheckInDateKey(bar) {
  if (bar?.startDate) {
    const m = moment(bar.startDate);
    if (m.isValid()) return m.format('YYYY-MM-DD');
  }
  const raw = bar?.booking?.check_in_date || bar?.booking?.start_date || bar?.booking?.start;
  if (raw) {
    const m = moment(raw);
    if (m.isValid()) return m.format('YYYY-MM-DD');
  }
  return null;
}

/**
 * Count room departures (checkout events) in the given columns.
 * Includes rooms that already turned over to a new stay on the same day — those no
 * longer classify as Out exclusively, but still count toward the Out tile.
 */
function countDeparturesInVisibleRange(rooms, bookingBars, dayCount, fromCol = 0) {
  const roomList = rooms || [];
  const days = Math.max(1, Number(dayCount) || 1);
  const startCol = Math.max(0, Number(fromCol) || 0);
  let departures = 0;

  roomList.forEach((room) => {
    const bars = roomBars(room, bookingBars);
    for (let col = startCol; col < days; col += 1) {
      const departed = bars.some((bar) => {
        const st = normalizeBarStatus(bar.status);
        if (!isDepartedBarStatus(st, bar)) return false;
        // Checkout morning in range, or same-day stay (CI=CO).
        if (barIsCheckoutDay(bar, col)) return true;
        if (isSameDayBar(bar) && barCoversStayNight(bar, col)) return true;
        return false;
      });
      if (departed) departures += 1;
    }
  });

  return departures;
}

/**
 * Bookings that checked in on a given date and later/already departed (Out).
 * Used so range-start arrivals (e.g. CI on the 2nd for a 2→8 board) always count.
 */
function countDepartedCheckInsOnDate(rooms, bookingBars, dateKey) {
  if (!dateKey) return 0;
  const target = String(dateKey);
  let count = 0;

  (rooms || []).forEach((room) => {
    const bars = roomBars(room, bookingBars);
    const hit = bars.some((bar) => {
      const st = normalizeBarStatus(bar.status);
      if (!isDepartedBarStatus(st, bar)) return false;
      // Ignore bars that only continue from before this date (startsBeforeCalendar
      // with true CI before range start) — require real CI day match.
      if (bar?.startsBeforeCalendar && barCheckInDateKey(bar) !== target) {
        return false;
      }
      return barCheckInDateKey(bar) === target;
    });
    if (hit) count += 1;
  });

  return count;
}

/**
 * Inventory snapshot for one focus day column in the bar calendar.
 * Uses the same buckets as Ultra Room Board.
 */
export function summarizeRoomBoardFromGridBars(rooms, bookingBars, focusCol = 0, dayContext = null) {
  const roomList = rooms || [];
  const counts = emptyRoomBoardGroupCounts(roomList.length);
  const context = {
    ...(dayContext || {}),
    rangeDays: dayContext?.rangeDays != null ? dayContext.rangeDays : 1,
  };

  roomList.forEach((room) => {
    const { status, hasNextBooking } = classifyRoomForFocusCol(
      room,
      bookingBars,
      focusCol,
      context,
    );
    applyRoomStatusToCounts(counts, status, hasNextBooking);
  });

  const accounted =
    counts.occupied
    + counts.hourly
    + counts.pending_checkout
    + counts.booked
    + counts.blocked
    + counts.checked_out
    + counts.cancelled
    + counts.vacant;
  if (accounted < counts.total) {
    counts.vacant += counts.total - accounted;
  }

  // Single-day board: Out tile includes every departure that day (turnover-safe).
  counts.departures_today = countDeparturesInVisibleRange(roomList, bookingBars, 1);

  return counts;
}

/**
 * Aggregate inventory across the visible calendar days (7 / 15 / 30 from col 0).
 * Unit = room-night. Total = rooms × days.
 * Same classify rules as a single day, summed across columns.
 *
 * @param {string[]} [dateKeys] YYYY-MM-DD per column (for Due = today/overdue only)
 * @param {string|null} [todayKey] hotel today YYYY-MM-DD
 */
export function summarizeRoomBoardFromGridBarsRange(
  rooms,
  bookingBars,
  dayCount = 1,
  dateKeys = null,
  todayKey = null,
) {
  const roomList = rooms || [];
  const days = Math.max(1, Number(dayCount) || 1);
  const today = todayKey || null;

  if (days <= 1) {
    const dayKey = Array.isArray(dateKeys) && dateKeys[0] ? dateKeys[0] : null;
    const counts = summarizeRoomBoardFromGridBars(roomList, bookingBars, 0, {
      dayKey,
      todayKey: today,
      rangeDays: 1,
    });
    // Single-day: Out tile includes every departure that day (turnover-safe).
    const departuresOnDay = countDeparturesInVisibleRange(roomList, bookingBars, 1, 0);
    const departedCheckIns = countDepartedCheckInsOnDate(roomList, bookingBars, dayKey);
    counts.departures_today = Math.max(
      Number(counts.departures_today) || 0,
      departuresOnDay,
      departedCheckIns,
    );
    return counts;
  }

  const counts = emptyRoomBoardGroupCounts(roomList.length * days);

  for (let col = 0; col < days; col += 1) {
    const dayKey = Array.isArray(dateKeys) && dateKeys[col] ? dateKeys[col] : null;
    roomList.forEach((room) => {
      const { status, hasNextBooking } = classifyRoomForFocusCol(
        room,
        bookingBars,
        col,
        { dayKey, todayKey: today, rangeDays: days },
      );
      applyRoomStatusToCounts(counts, status, hasNextBooking);
    });
  }

  const accounted =
    counts.occupied
    + counts.hourly
    + counts.pending_checkout
    + counts.booked
    + counts.blocked
    + counts.checked_out
    + counts.cancelled
    + counts.vacant;
  if (accounted < counts.total) {
    counts.vacant += counts.total - accounted;
  }

  // Multi-day: Out is stay-night based — do not inflate with departure events.
  counts.departures_today = null;

  return counts;
}

function boardStatShellSx(isDark, accent, { dense = false, compact = false } = {}) {
  return {
    display: 'inline-flex',
    flexDirection: 'column',
    justifyContent: 'center',
    minHeight: dense ? 30 : compact ? 36 : 40,
    px: dense ? 0.55 : compact ? 0.85 : 1.05,
    py: dense ? 0.2 : compact ? 0.35 : 0.45,
    borderRadius: dense ? '8px' : '10px',
    border: `1px solid ${alpha(accent, isDark ? 0.38 : 0.28)}`,
    background: isDark
      ? `linear-gradient(160deg, ${alpha(accent, 0.16)} 0%, ${alpha('#0b1220', 0.55)} 72%)`
      : `linear-gradient(160deg, ${alpha(accent, 0.12)} 0%, ${alpha('#ffffff', 0.92)} 75%)`,
    boxShadow: dense
      ? 'none'
      : isDark
        ? `inset 0 1px 0 ${alpha('#fff', 0.04)}, 0 4px 12px ${alpha('#000', 0.22)}`
        : `inset 0 1px 0 ${alpha('#fff', 0.8)}, 0 2px 8px ${alpha(accent, 0.12)}`,
  };
}

function BoardStatValue({ isDark, color, children, dense = false, compact = false }) {
  return (
    <Typography
      component="span"
      sx={{
        fontWeight: 800,
        fontSize: dense ? '0.78rem' : compact ? '0.92rem' : '1.02rem',
        lineHeight: 1.05,
        letterSpacing: '-0.02em',
        color: color || (isDark ? '#f8fafc' : '#0f172a'),
        fontVariantNumeric: 'tabular-nums',
      }}
    >
      {children}
    </Typography>
  );
}

function BoardStatLabel({ isDark, children, dense = false, compact = false }) {
  return (
    <Typography
      component="span"
      sx={{
        mt: dense ? 0.05 : 0.15,
        fontSize: dense ? '0.5rem' : compact ? '0.58rem' : '0.62rem',
        fontWeight: 700,
        letterSpacing: dense ? '0.02em' : '0.04em',
        textTransform: 'uppercase',
        color: isDark ? alpha('#e2e8f0', 0.62) : alpha('#334155', 0.72),
        lineHeight: 1.1,
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </Typography>
  );
}

/**
 * Shared Room Board inventory tiles (Ultra + bar calendar).
 */
export default function RoomBoardInventoryStats({
  groupCounts,
  themeMode = 'dark',
  dense = false,
  compact = false,
  isLoading = false,
  justifyContent = 'flex-end',
  /** When > 1, counts are room-days for the visible calendar range. */
  rangeDays: _rangeDays = 1,
}) {
  const isDark = themeMode === 'dark';
  const sizeProps = { dense, compact };
  const summary = buildRoomBoardSummary(groupCounts || emptyRoomBoardGroupCounts());
  const {
    total,
    blocked,
    inHouse,
    booked,
    available,
    open,
    out,
    due,
    occupancyPct,
  } = summary;
  const totalLabel = dense ? 'Total' : 'Total Nights';

  const totalAccent = isDark ? '#94a3b8' : '#64748b';
  const bookedAccent = ROOM_BOARD_ACCENT.booked;
  const inHouseAccent = ROOM_BOARD_ACCENT.occupied;
  const occupancyAccent = DASHBOARD_ACCENT;
  const availableAccent = isDark ? '#38bdf8' : '#0284c7';
  const blockedAccent = ROOM_BOARD_ACCENT.blocked;

  const valueOrSkeleton = (value, width = 18) => (
    isLoading ? (
      <Skeleton
        variant="rounded"
        animation="wave"
        width={dense ? Math.max(10, width - 4) : width}
        height={dense ? 12 : compact ? 14 : 16}
        sx={calendarDataBoneSx(isDark)}
      />
    ) : value
  );

  const cellPad = dense
    ? { px: 0.45, py: 0.15 }
    : compact
      ? { px: 0.85, py: 0.35 }
      : { px: 1, py: 0.45 };

  return (
    <Box
      sx={{
        display: 'flex',
        gap: dense ? 0.35 : 0.65,
        flexWrap: 'wrap',
        alignItems: 'stretch',
        justifyContent,
        width: dense ? '100%' : 'auto',
        minWidth: 0,
        pointerEvents: 'none',
      }}
    >
      <Box sx={boardStatShellSx(isDark, totalAccent, sizeProps)}>
        <BoardStatValue isDark={isDark} color={isDark ? '#e2e8f0' : '#0f172a'} {...sizeProps}>
          {valueOrSkeleton(total)}
        </BoardStatValue>
        <BoardStatLabel isDark={isDark} {...sizeProps}>{totalLabel}</BoardStatLabel>
      </Box>

      <Box
        sx={{
          ...boardStatShellSx(isDark, availableAccent, sizeProps),
          flexDirection: 'row',
          alignItems: 'stretch',
          gap: 0,
          px: 0,
          py: 0,
          overflow: 'hidden',
          minWidth: dense
            ? (due > 0 ? 156 : 128)
            : compact
              ? (due > 0 ? 210 : 168)
              : (due > 0 ? 236 : 190),
        }}
      >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              ...cellPad,
              px: dense ? 0.55 : cellPad.px,
              minWidth: dense ? 42 : compact ? 58 : 66,
              background: alpha(availableAccent, isDark ? 0.14 : 0.1),
            }}
          >
            <BoardStatValue isDark={isDark} color={isDark ? '#7dd3fc' : '#0369a1'} {...sizeProps}>
              {valueOrSkeleton(available)}
            </BoardStatValue>
            <BoardStatLabel isDark={isDark} {...sizeProps}>{dense ? 'Avail' : 'Available'}</BoardStatLabel>
          </Box>
          <Box
            sx={{
              width: '1px',
              alignSelf: 'stretch',
              bgcolor: alpha(isDark ? '#fff' : '#000', isDark ? 0.12 : 0.08),
            }}
          />
          {(
            [
              { key: 'open', label: 'Open', value: open, color: isDark ? '#e2e8f0' : '#0f172a' },
              { key: 'out', label: 'Out', value: out, color: isDark ? '#90caf9' : '#1565c0' },
              ...(due > 0 || isLoading
                ? [{ key: 'due', label: 'Due', value: due, color: isDark ? '#e6c200' : '#8a6d00' }]
                : []),
            ]
          ).map((part, index) => (
            <Box
              key={part.key}
              sx={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                alignItems: 'center',
                ...cellPad,
                minWidth: dense ? 32 : compact ? 44 : 50,
                borderLeft: index === 0
                  ? 'none'
                  : `1px solid ${alpha(isDark ? '#fff' : '#000', isDark ? 0.08 : 0.06)}`,
                background:
                  part.key === 'due'
                    ? alpha(ROOM_BOARD_ACCENT.pending_checkout, isDark ? 0.12 : 0.1)
                    : part.key === 'out'
                      ? alpha(ROOM_BOARD_ACCENT.checked_out, isDark ? 0.1 : 0.08)
                      : 'transparent',
              }}
            >
              <BoardStatValue isDark={isDark} color={part.color} {...sizeProps}>
                {valueOrSkeleton(part.value, 14)}
              </BoardStatValue>
              <BoardStatLabel isDark={isDark} {...sizeProps}>{part.label}</BoardStatLabel>
            </Box>
          ))}
      </Box>

      <Box
        sx={{
          ...boardStatShellSx(isDark, bookedAccent, sizeProps),
          flexDirection: 'row',
          alignItems: 'stretch',
          gap: 0,
          px: 0,
          py: 0,
          overflow: 'hidden',
          minWidth: dense ? 108 : compact ? 148 : 168,
        }}
      >
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            ...cellPad,
            background: alpha(bookedAccent, isDark ? 0.1 : 0.08),
          }}
        >
          <BoardStatValue isDark={isDark} color={isDark ? '#ffcc80' : '#e65100'} {...sizeProps}>
            {valueOrSkeleton(booked)}
          </BoardStatValue>
          <BoardStatLabel isDark={isDark} {...sizeProps}>Booked</BoardStatLabel>
        </Box>
        <Box
          sx={{
            width: '1px',
            alignSelf: 'stretch',
            bgcolor: alpha(isDark ? '#fff' : '#000', isDark ? 0.12 : 0.08),
          }}
        />
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            ...cellPad,
            background: alpha(inHouseAccent, isDark ? 0.12 : 0.1),
          }}
        >
          <BoardStatValue isDark={isDark} color={isDark ? '#a5d6a7' : '#1b5e20'} {...sizeProps}>
            {valueOrSkeleton(inHouse)}
          </BoardStatValue>
          <BoardStatLabel isDark={isDark} {...sizeProps}>{dense ? 'In-H' : 'In House'}</BoardStatLabel>
        </Box>
      </Box>

      <Box sx={boardStatShellSx(isDark, occupancyAccent, sizeProps)}>
        <BoardStatValue isDark={isDark} color={isDark ? occupancyAccent : '#0f766e'} {...sizeProps}>
          {valueOrSkeleton(`${Number(occupancyPct).toFixed(2)}%`, 36)}
        </BoardStatValue>
        <BoardStatLabel isDark={isDark} {...sizeProps}>{dense ? 'Occ.' : 'Occupancy'}</BoardStatLabel>
      </Box>

      <Box sx={boardStatShellSx(isDark, blockedAccent, sizeProps)}>
        <BoardStatValue isDark={isDark} color={isDark ? '#ef9a9a' : '#c62828'} {...sizeProps}>
          {valueOrSkeleton(blocked)}
        </BoardStatValue>
        <BoardStatLabel isDark={isDark} {...sizeProps}>{dense ? 'Block' : 'Blocked'}</BoardStatLabel>
      </Box>
    </Box>
  );
}
